import React, { type FC, type PropsWithChildren } from 'react';

import { getColor } from '@lucid/theme';
import { withImage } from '@lucid/hooks';

import Box from '../../box/box';
import Heading from '../../heading/heading';
import Image from '../../image/image';
import Link from '../../link/link';
import Text from '../../text/text';
import type { StatGridProps } from '../stat-grid';

const CardsStatGrid: FC<PropsWithChildren<StatGridProps>> = ({
  headingHighlightColor,
  heading,
  text,
  note,
  link,
  items,
  statColor,
}) => (
  <>
    <Box css={{ maxWidth: '740px' }}>
      {heading && (
        <Heading
          as="h2"
          css={{ color: 'white' }}
          highlightTextColor={headingHighlightColor}
          text={heading}
          variant="hero2XL700"
        />
      )}
      {text && (
        <Text css={theme => ({ color: theme.colors.white, margin: `${theme.space.s40}px 0` })} variant="paragraphL400">
          {text}
        </Text>
      )}
      {link && (
        <Box>
          <Link displayAsButton href={link?.final_url} variant="secondary">
            {link?.default_link_text}
          </Link>
        </Box>
      )}
    </Box>
    <Box
      css={theme => ({
        label: 'statGridCards',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: theme.space.s80,
        paddingBottom: theme.space.s40,
        [theme.breakpoints.bp1024.min]: {
          gridTemplateColumns: 'repeat(auto-fit, 380px)',
          flexDirection: 'row',
          flexWrap: 'wrap',
        },
      })}
      data-testid="stat-grid-cards"
    >
      {items?.map(({ heading, text, image }, i) => (
        <Box
          key={`stat-grid-item-cards-${i}`}
          css={theme => ({
            width: '290px',
            height: '290px',
            background: 'white',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: theme.space.s24,
            borderRadius: theme.radii.r16,
            [theme.breakpoints.bp500.min]: {
              width: '380px',
              height: '380px',
              padding: theme.space.s48,
            },
          })}
        >
          {image && (
            <Image
              css={theme => ({
                width: '64px',
                height: '64px',
                marginBottom: theme.space.s8,
              })}
              imageAlt={image.translations?.[0]?.alt || ''}
              imageSrc={withImage(image)}
            />
          )}
          <hgroup>
            {heading && (
              <Heading
                as="h3"
                css={theme => ({
                  color: getColor({
                    color: statColor,
                    theme,
                  }),
                  fontWeight: theme.fontWeights.fw300,
                  fontSize: '64px',
                  marginBottom: theme.space.s8,
                  lineHeight: 1,
                })}
                variant="headingM"
              >
                {heading}
              </Heading>
            )}
            {text && (
              <Text
                aria-level={3}
                css={theme => ({ fontWeight: theme.fontWeights.fw500, margin: 0 })}
                role="heading"
                variant="headingL"
              >
                {text}
              </Text>
            )}
          </hgroup>
        </Box>
      ))}
    </Box>
    {note && (
      <Text
        css={theme => ({
          color: 'white',
          fontSize: '10px',
          [theme.breakpoints.bp1024.min]: {
            maxWidth: '780px',
          },
        })}
      >
        {note}
      </Text>
    )}
  </>
);

export { CardsStatGrid };
export default CardsStatGrid;
